import { useState } from "react";
import { Card } from "@tremor/react";
import { ScrollText, CheckCircle2, AlertTriangle, Loader2, Clock } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useAudits, useAudit, type Audit, type Finding } from "@/lib/queries";
import { cn } from "@/lib/utils";

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleString([], {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

const SEV_BADGE: Record<string, string> = {
  critical: "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/40",
  high:     "bg-orange-500/15 text-orange-700 dark:text-orange-400 border-orange-500/40",
  medium:   "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/40",
  low:      "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/40",
  info:     "bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/40",
};

export default function AuditsPage() {
  const list = useAudits({ limit: 50 });
  const [activeId, setActiveId] = useState<number | null>(null);
  const detail = useAudit(activeId);

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <div className="lg:col-span-1">
        <h1 className="text-2xl font-semibold tracking-tight">Audits</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Recent audit runs across your properties. Click one to view the
          narrative + findings.
        </p>
        <div className="mt-4 space-y-2">
          {list.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {list.data?.items.length === 0 && (
            <Card>
              <p className="text-sm text-muted-foreground">
                No audits yet. Trigger one from the Properties tab.
              </p>
            </Card>
          )}
          {list.data?.items.map((a) => (
            <AuditRowMini
              key={a.id}
              audit={a}
              active={activeId === a.id}
              onClick={() => setActiveId(a.id)}
            />
          ))}
        </div>
      </div>

      <div className="lg:col-span-2">
        {detail.data ? (
          <AuditDetail audit={detail.data} />
        ) : activeId ? (
          <Card><p className="text-sm text-muted-foreground">Loading audit…</p></Card>
        ) : (
          <Card>
            <p className="text-sm text-muted-foreground">
              Select an audit on the left to view its narrative + findings.
            </p>
          </Card>
        )}
      </div>
    </div>
  );
}

function AuditRowMini({ audit, active, onClick }: { audit: Audit; active: boolean; onClick: () => void }) {
  const findingsCount = (audit.metadata as { findings_count?: number })?.findings_count ?? 0;
  const status = audit.status; // server-derived in _derive_audit_status

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "w-full rounded-md border bg-card px-3 py-2.5 text-left transition-colors",
        active ? "border-primary ring-1 ring-primary/40" : "border-border hover:bg-accent/40",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <ScrollText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="truncate text-sm font-medium">
              {audit.display_prompt ?? `Audit #${audit.id}`}
            </span>
          </div>
          <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
            <Clock className="h-3 w-3" />
            <span>{fmtTime(audit.run_at)}</span>
            {audit.duration_s !== null && <span>· {audit.duration_s.toFixed(0)}s</span>}
            {findingsCount > 0 && <span>· {findingsCount} finding{findingsCount === 1 ? "" : "s"}</span>}
          </div>
        </div>
        <div className="shrink-0">
          {status === "ok" && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
          {status === "error" && <AlertTriangle className="h-4 w-4 text-red-500" />}
          {status === "running" && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
        </div>
      </div>
    </button>
  );
}

function AuditDetail({ audit }: { audit: { findings: Finding[] } & Audit }) {
  return (
    <div className="space-y-4">
      <Card>
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">{audit.display_prompt ?? `Audit #${audit.id}`}</h2>
            <p className="text-xs text-muted-foreground">
              {fmtTime(audit.run_at)} · {audit.duration_s !== null ? `${audit.duration_s.toFixed(1)}s` : "running…"} ·
              {audit.ok ? " ok" : audit.error ? " error" : " in progress"}
            </p>
          </div>
        </div>
        {audit.error && (
          <div className="mt-3 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-700 dark:text-red-400">
            {audit.error}
          </div>
        )}
      </Card>

      {audit.findings.length > 0 && (
        <Card>
          <h3 className="text-sm font-semibold">Findings ({audit.findings.length})</h3>
          <div className="mt-3 space-y-2">
            {audit.findings.map((f) => (
              <FindingMini key={f.id} f={f} />
            ))}
          </div>
        </Card>
      )}

      {audit.output ? (
        <Card>
          <div className="prose prose-sm max-w-none dark:prose-invert prose-headings:scroll-mt-4">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{audit.output}</ReactMarkdown>
          </div>
        </Card>
      ) : audit.error ? (
        <Card>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <AlertTriangle className="h-4 w-4 text-red-500" />
            Audit failed before the narrative could be generated. See error above.
          </div>
        </Card>
      ) : (
        <Card>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Auriel is generating the audit narrative…
          </div>
        </Card>
      )}
    </div>
  );
}

export function FindingMini({ f }: { f: Finding }) {
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className={cn(
            "rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
            SEV_BADGE[f.severity] ?? SEV_BADGE.info,
          )}>{f.severity}</span>
          <span className="font-mono text-[10px] text-muted-foreground">{f.kind}</span>
        </div>
      </div>
      <p className="mt-1.5 text-sm font-medium">{f.title}</p>
      {f.detail && (
        <div className="mt-2 prose prose-sm max-w-none text-xs dark:prose-invert">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{f.detail}</ReactMarkdown>
        </div>
      )}
    </div>
  );
}
